import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Icon } from '@/components/r5/glyphs'
import { useAuth } from '@/features/auth/auth-context'
import { dateLocale, t, useLanguage } from '@/lib/i18n'
import { announceViewAs, listenViewAs, tab } from '@/lib/view-as-sync'
import './view-as-banner.css'

const BAR_PX = 40
/** `setTimeout` keeps a signed 32-bit delay. */
const MAX_TIMEOUT = 2_147_483_647

/** HH:mm in the viewer's own time zone, Western digits in both languages. */
function clock(date: Date) {
  return new Intl.DateTimeFormat(dateLocale(), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date)
}

function Bar({ targetName, expiresAt }: { targetName: string; expiresAt: string }) {
  const language = useLanguage()
  const { exitViewAs } = useAuth()
  const barRef = useRef<HTMLDivElement>(null)
  const leaving = useRef(false)
  const [ended, setEnded] = useState(false)
  const [busy, setBusy] = useState(false)
  const endMs = Date.parse(expiresAt)

  const leave = useCallback((expired: boolean) => {
    if (leaving.current) return
    leaving.current = true
    if (expired) setEnded(true)
    else setBusy(true)
    void exitViewAs()
  }, [exitViewAs])

  // Other tabs of this browser learn that this View as started (they reload and show the bar too).
  useEffect(() => { announceViewAs(expiresAt) }, [expiresAt])

  // The bar reserves its height for the shell (sticky bars sit under it) for as long as it is mounted.
  useLayoutEffect(() => {
    const root = document.documentElement
    const bar = barRef.current
    const apply = () => root.style.setProperty('--view-as-h', `${Math.max(BAR_PX, Math.round(bar?.getBoundingClientRect().height ?? 0))}px`)
    apply()
    const observer = typeof ResizeObserver === 'function' && bar ? new ResizeObserver(apply) : null
    if (bar) observer?.observe(bar)
    return () => {
      observer?.disconnect()
      root.style.removeProperty('--view-as-h')
    }
  }, [])

  // At the end time the same exit runs on its own, so a stale tab cannot go on as someone else.
  useEffect(() => {
    const wait = Math.min(Math.max(endMs - Date.now(), 0), MAX_TIMEOUT)
    const timer = window.setTimeout(() => leave(true), wait)
    return () => window.clearTimeout(timer)
  }, [endMs, leave])

  const [before, after = ''] = t('Viewing as {0}. Actions are recorded as you.').split('{0}')

  return (
    <div ref={barRef} className="view-as-banner" role="status" aria-live="polite" lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Icon name="eye" size={18} className="view-as-banner__icon" />
      {ended ? (
        <p className="view-as-banner__text">{t('View as has ended.')}</p>
      ) : (
        <>
          <p className="view-as-banner__text">{before}<strong dir="auto" data-content="">{targetName}</strong>{after}</p>
          <p className="view-as-banner__ends">{t('Ends at {0}', { 0: clock(new Date(endMs)) })}</p>
          <button type="button" className="view-as-banner__exit" disabled={busy} onClick={() => leave(false)}>{t('Exit')}</button>
        </>
      )}
    </div>
  )
}

/**
 * The View as bar (VIEW-AS-SPEC 5): a full-width warning strip above every route while a superadmin uses IELTS Mock as a
 * student, a teacher or an admin. It shows who, when it ends, and Exit; at the end time it exits by itself. Nothing is
 * rendered for a normal session. In the exam it takes a row of its own (view-as-banner.css), so it never covers the exam.
 */
export function ViewAsBanner() {
  const { impersonation, profile } = useAuth()
  // What this tab knows: the end time of its View as ('0' for a normal session), and whether anyone is signed in at all.
  const known = useRef({ value: '0', signedIn: false })
  known.current = { value: impersonation ? impersonation.expires_at : '0', signedIn: Boolean(profile) }

  // Another tab started or ended a View as in the shared cookie jar: this tab's app is for the wrong person. Reload it.
  useEffect(() => listenViewAs((value) => {
    if (known.current.signedIn && value !== known.current.value) tab.reload()
  }), [])

  if (!impersonation) return null
  return <Bar targetName={impersonation.target_name} expiresAt={impersonation.expires_at} />
}
